import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import {
  Search,
  ChevronDown,
  SlidersHorizontal,
  LayoutGrid,
} from 'lucide-react';
import { MapFilterBarProps } from '../../types';

interface ExtendedMapFilterBarProps extends MapFilterBarProps {
  onViewChange?: (view: 'grid' | 'map') => void;
}

const MapFilterBar: React.FC<ExtendedMapFilterBarProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  propertyCount,
  onToggleSidebar,
  showSidebar,
  onViewChange,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');

  // Local state for price slider
  const [localPriceMin, setLocalPriceMin] = useState(filters.priceMin);
  const [localPriceMax, setLocalPriceMax] = useState(filters.priceMax);

  // Suppress unused variable warnings
  void propertyCount;
  void onToggleSidebar;
  void showSidebar;

  const hasActiveFilters =
    filters.propertyType !== 'all' ||
    filters.priceMin > 0 ||
    filters.priceMax < 2000000 ||
    filters.bedrooms !== 'any' ||
    filters.bathrooms !== 'any' ||
    filters.sqftMin !== '' ||
    filters.sqftMax !== '' ||
    filters.yearBuiltMin !== '' ||
    filters.yearBuiltMax !== '' ||
    filters.lotSize !== 'any' ||
    filters.hasHOA !== 'any' ||
    filters.hoaFeesMax !== '' ||
    filters.parkingMin !== '' ||
    filters.keywords !== '' ||
    filters.daysOnMarket !== 'any' ||
    filters.openHousesOnly ||
    filters.virtualToursOnly;

  const activeFilterCount = [
    filters.propertyType !== 'all',
    filters.priceMin > 0 || filters.priceMax < 2000000,
    filters.bedrooms !== 'any',
    filters.bathrooms !== 'any',
    filters.sqftMin !== '' || filters.sqftMax !== '',
    filters.yearBuiltMin !== '' || filters.yearBuiltMax !== '',
    filters.lotSize !== 'any',
    filters.hasHOA !== 'any',
    filters.parkingMin !== '',
    filters.keywords !== '',
    filters.daysOnMarket !== 'any',
    filters.openHousesOnly,
    filters.virtualToursOnly,
  ].filter(Boolean).length;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchValue) {
      newParams.set('search', searchValue);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  // Format price for display
  const formatPrice = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  // Zillow-style filter button
  const FilterButton = ({
    children,
    isActive = false,
    onClick,
    className = '',
  }: {
    children: React.ReactNode;
    isActive?: boolean;
    onClick?: () => void;
    className?: string;
  }) => (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center justify-center gap-1 h-10 px-4 text-sm font-medium
        border rounded-lg transition-colors whitespace-nowrap
        ${
          isActive
            ? 'bg-blue-50 border-blue-600 text-blue-700'
            : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'
        }
        ${className}
      `}
    >
      {children}
    </button>
  );

  // Handle price slider change
  const handlePriceSliderChange = (values: number[]) => {
    setLocalPriceMin(values[0]);
    setLocalPriceMax(values[1]);
  };

  // Apply price filter
  const applyPriceFilter = () => {
    onFilterChange('priceMin', localPriceMin);
    onFilterChange('priceMax', localPriceMax);
  };

  // Advanced Filters Sheet Content - Now includes Price and Beds/Baths
  const AdvancedFiltersContent = () => (
    <div className="space-y-6">
      {/* Price Range with Slider */}
      <div>
        <Label className="text-sm font-medium text-gray-900 mb-3 block">
          {t('filters.priceRange')}
        </Label>

        {/* Min/Max Input Fields */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1">
            <Label className="text-xs text-gray-500 mb-1 block">{t('common.min')}</Label>
            <Input
              type="text"
              value={formatPrice(localPriceMin)}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                const num = parseInt(value) || 0;
                setLocalPriceMin(Math.min(num, localPriceMax));
              }}
              className="text-center"
            />
          </div>
          <span className="text-gray-400 mt-5">-</span>
          <div className="flex-1">
            <Label className="text-xs text-gray-500 mb-1 block">{t('common.max')}</Label>
            <Input
              type="text"
              value={localPriceMax >= 2000000 ? `$2M+` : formatPrice(localPriceMax)}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                const num = parseInt(value) || 0;
                setLocalPriceMax(Math.max(num, localPriceMin));
              }}
              className="text-center"
            />
          </div>
        </div>

        {/* Dual Range Slider */}
        <div className="px-2 py-3">
          <Slider
            defaultValue={[localPriceMin, localPriceMax]}
            value={[localPriceMin, localPriceMax]}
            min={0}
            max={2000000}
            step={25000}
            onValueChange={handlePriceSliderChange}
            onValueCommit={applyPriceFilter}
          />
        </div>

        {/* Price Labels */}
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>$0</span>
          <span>$500K</span>
          <span>$1M</span>
          <span>$1.5M</span>
          <span>$2M+</span>
        </div>

        {/* Quick Select Buttons */}
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            { label: t('common.any'), min: 0, max: 2000000 },
            { label: '$0-$300K', min: 0, max: 300000 },
            { label: '$300K-$500K', min: 300000, max: 500000 },
            { label: '$500K-$750K', min: 500000, max: 750000 },
            { label: '$750K+', min: 750000, max: 2000000 },
          ].map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setLocalPriceMin(preset.min);
                setLocalPriceMax(preset.max);
                onFilterChange('priceMin', preset.min);
                onFilterChange('priceMax', preset.max);
              }}
              className={`
                px-3 py-1.5 rounded text-xs font-medium border transition-colors
                ${
                  filters.priceMin === preset.min && filters.priceMax === preset.max
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }
              `}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bedrooms */}
      <div>
        <Label className="text-sm font-medium text-gray-900 mb-3 block">
          {t('common.bedrooms')}
        </Label>
        <div className="flex gap-2">
          {['any', '1', '2', '3', '4', '5'].map((bed) => (
            <button
              key={bed}
              onClick={() => onFilterChange('bedrooms', bed)}
              className={`
                flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors
                ${
                  filters.bedrooms === bed
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }
              `}
            >
              {bed === 'any' ? t('common.any') : `${bed}+`}
            </button>
          ))}
        </div>
      </div>

      {/* Bathrooms */}
      <div>
        <Label className="text-sm font-medium text-gray-900 mb-3 block">
          {t('common.bathrooms')}
        </Label>
        <div className="flex gap-2">
          {['any', '1', '2', '3', '4'].map((bath) => (
            <button
              key={bath}
              onClick={() => onFilterChange('bathrooms', bath)}
              className={`
                flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors
                ${
                  filters.bathrooms === bath
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }
              `}
            >
              {bath === 'any' ? t('common.any') : `${bath}+`}
            </button>
          ))}
        </div>
      </div>

      {/* Square Footage */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          {t('filters.sqft')}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={t('common.min')}
            value={filters.sqftMin}
            onChange={(e) => onFilterChange('sqftMin', e.target.value)}
            className="flex-1"
          />
          <span className="text-gray-500">-</span>
          <Input
            type="number"
            placeholder={t('common.max')}
            value={filters.sqftMax}
            onChange={(e) => onFilterChange('sqftMax', e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      {/* Year Built */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          {t('filters.yearBuilt')}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder={t('common.min')}
            value={filters.yearBuiltMin}
            onChange={(e) => onFilterChange('yearBuiltMin', e.target.value)}
            className="flex-1"
          />
          <span className="text-gray-500">-</span>
          <Input
            type="number"
            placeholder={t('common.max')}
            value={filters.yearBuiltMax}
            onChange={(e) => onFilterChange('yearBuiltMax', e.target.value)}
            className="flex-1"
          />
        </div>
      </div>

      {/* Additional Filters Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Lot Size */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t('filters.lotSize')}
          </Label>
          <Select
            value={filters.lotSize}
            onValueChange={(value) => onFilterChange('lotSize', value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('common.any')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('common.any')}</SelectItem>
              <SelectItem value="small (< 5,000 sqft)">{t('filters.lotSizeSmall')}</SelectItem>
              <SelectItem value="medium (5,000-10,000 sqft)">{t('filters.lotSizeMedium')}</SelectItem>
              <SelectItem value="large (> 10,000 sqft)">{t('filters.lotSizeLarge')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* HOA */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t('filters.hoa')}
          </Label>
          <Select
            value={filters.hasHOA}
            onValueChange={(value) => onFilterChange('hasHOA', value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('common.any')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('common.any')}</SelectItem>
              <SelectItem value="yes">{t('filters.hasHOA')}</SelectItem>
              <SelectItem value="no">{t('filters.noHOA')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Parking */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t('filters.minParking')}
          </Label>
          <Input
            type="number"
            placeholder={t('common.any')}
            value={filters.parkingMin}
            onChange={(e) => onFilterChange('parkingMin', e.target.value)}
            min="0"
            className="w-full"
          />
        </div>

        {/* Days on Market */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t('filters.daysOnMarket')}
          </Label>
          <Select
            value={filters.daysOnMarket}
            onValueChange={(value) => onFilterChange('daysOnMarket', value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('common.any')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('common.any')}</SelectItem>
              <SelectItem value="new (< 7 days)">{t('filters.daysNew')}</SelectItem>
              <SelectItem value="recent (< 14 days)">{t('filters.daysRecent')}</SelectItem>
              <SelectItem value="active (< 30 days)">{t('filters.daysActive')}</SelectItem>
              <SelectItem value="all">{t('filters.daysAll')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Keywords Search */}
      <div>
        <Label className="text-sm font-medium text-gray-700 mb-2 block">
          {t('filters.keywords')}
        </Label>
        <Input
          type="text"
          placeholder={t('filters.keywordsPlaceholder')}
          value={filters.keywords}
          onChange={(e) => onFilterChange('keywords', e.target.value)}
          className="w-full"
        />
      </div>

      {/* Checkboxes */}
      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.openHousesOnly}
            onChange={(e) => onFilterChange('openHousesOnly', e.target.checked)}
            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            {t('filters.openHousesOnly')}
          </span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.virtualToursOnly}
            onChange={(e) =>
              onFilterChange('virtualToursOnly', e.target.checked)
            }
            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">
            {t('filters.virtualToursOnly')}
          </span>
        </label>
      </div>

      {/* Clear All */}
      {hasActiveFilters && (
        <Button onClick={onClearFilters} variant="outline" className="w-full">
          {t('filters.clearAllFilters')}
        </Button>
      )}
    </div>
  );

  return (
    <div className="absolute top-0 left-0 right-0 z-10 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center h-16 px-4 gap-3">
        {/* Back to Grid View Button */}
        {onViewChange && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewChange('grid')}
            className="h-10 px-3 rounded-lg border-gray-300 hover:bg-gray-50"
            title={t('mapSearch.listView')}
          >
            <LayoutGrid className="h-5 w-5" />
          </Button>
        )}

        {/* Search Input */}
        <form onSubmit={handleSearch} className="relative flex-1 max-w-[340px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t('search.placeholder')}
            className="pl-10 pr-4 h-10 w-full border-gray-300 rounded-lg bg-white"
          />
        </form>

        {/* Filter Buttons - Desktop */}
        <div className="hidden lg:flex items-center gap-2">
          {/* Listing Type (For Sale) */}
          <Select value="sale" onValueChange={() => {}}>
            <SelectTrigger className="h-10 px-4 rounded-lg border-gray-300 bg-white hover:bg-gray-50 min-w-[100px] gap-1">
              <SelectValue>{t('listingTypes.forSale')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sale">{t('listingTypes.forSale')}</SelectItem>
              <SelectItem value="rent">{t('listingTypes.forRent')}</SelectItem>
            </SelectContent>
          </Select>

          {/* Home Type */}
          <Select
            value={filters.propertyType}
            onValueChange={(value) => onFilterChange('propertyType', value)}
          >
            <SelectTrigger
              className={`h-10 px-4 rounded-lg border min-w-[140px] gap-1 ${
                filters.propertyType !== 'all'
                  ? 'bg-blue-50 border-blue-600 text-blue-700'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              }`}
            >
              <SelectValue>
                {filters.propertyType === 'all'
                  ? t('mapSearch.homeType')
                  : t(`propertyTypes.${filters.propertyType}`)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
              <SelectItem value="house">{t('propertyTypes.house')}</SelectItem>
              <SelectItem value="apartment">{t('propertyTypes.apartment')}</SelectItem>
              <SelectItem value="condo">{t('propertyTypes.condo')}</SelectItem>
              <SelectItem value="townhouse">{t('propertyTypes.townhouse')}</SelectItem>
            </SelectContent>
          </Select>

          {/* More Filters - Now the main filters button */}
          <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
            <SheetTrigger asChild>
              <FilterButton isActive={activeFilterCount > 0}>
                <SlidersHorizontal className="h-4 w-4 mr-1" />
                {t('filters.filtersLabel')}
                {activeFilterCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown className="h-4 w-4 ml-1" />
              </FilterButton>
            </SheetTrigger>
            <SheetContent side="right" className="w-[400px] sm:w-[450px] overflow-y-auto">
              <SheetHeader className="mb-6">
                <SheetTitle className="text-xl font-bold">
                  {t('filters.filtersLabel')}
                </SheetTitle>
              </SheetHeader>
              <AdvancedFiltersContent />
            </SheetContent>
          </Sheet>
        </div>

        {/* Mobile Filters Button */}
        <Sheet open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden h-10 px-4 rounded-lg"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              {t('filters.filtersLabel')}
              {activeFilterCount > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-2">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
        </Sheet>

        {/* Save Search Button - Right side */}
        <Button
          className="ml-auto bg-blue-600 hover:bg-blue-700 text-white h-10 px-4 rounded-lg font-medium hidden xl:flex whitespace-nowrap flex-shrink-0"
        >
          {t('mapSearch.saveSearch')}
        </Button>
      </div>
    </div>
  );
};

export default MapFilterBar;
